import {useHierarchy} from '@domain/kpis'
import {useUpmPlantsHierarchy} from '@domain/upm'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useMemo} from 'react'
import {useNavigate} from 'react-router'

import {AreaNavigation} from '../types'
import {mapKpiAreaToUpmArea, mapUpmAreaToKpiArea} from '../utils/area-hierarchy.utils'

type Params = {
  upmAreaId: string | undefined
  getNodeLink: (upmHierarchyId: string) => string
}

/** Navigation for hosts keyed by UPM hierarchy ids (Suite): maps the UPM area to its KPI area and back. */
export const useUpmHierarchyAreaNavigation = ({upmAreaId, getNodeLink}: Params): AreaNavigation => {
  const navigate = useNavigate()
  const upmPlantsMap = useUpmPlantsContext()
  const {data: upmHierarchyData, isLoading: isUpmHierarchyLoading} = useUpmPlantsHierarchy()
  const {data: kpiHierarchy, isLoading: isKpiHierarchyLoading} = useHierarchy()

  const upmPlants = useMemo(() => Object.values(upmPlantsMap), [upmPlantsMap])
  const upmHierarchy = upmHierarchyData?.hierarchy

  const areaId = useMemo(
    () =>
      upmAreaId && upmHierarchy && kpiHierarchy
        ? mapUpmAreaToKpiArea({upmAreaId, upmHierarchy, kpiHierarchy, upmPlants})
        : undefined,
    [upmAreaId, upmHierarchy, kpiHierarchy, upmPlants]
  )

  const getUpmAreaId = (kpiAreaId: string) =>
    upmHierarchy && kpiHierarchy
      ? mapKpiAreaToUpmArea({kpiAreaId, upmHierarchy, kpiHierarchy, upmPlants})
      : undefined

  const getAreaLink = (kpiAreaId: string) => {
    const upmId = getUpmAreaId(kpiAreaId)
    return upmId ? getNodeLink(upmId) : ''
  }

  return {
    areaId,
    isLoading: isUpmHierarchyLoading || isKpiHierarchyLoading,
    getPlantLink: (plantId) => {
      const upmId = upmPlants.find((plant) => plant.plantId === plantId)?.upmId
      return upmId ? getNodeLink(upmId) : ''
    },
    getAreaLink,
    onAreaChange: (kpiAreaId) => {
      const link = getAreaLink(kpiAreaId)
      if (link) void navigate(link)
    }
  }
}
